import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import NotificationCenter from '../../components/ui/NotificationCenter';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import SystemStatusIndicator from '../../components/ui/SystemStatusIndicator';
import SystemMetricsHeader from './components/SystemMetricsHeader';
import FilterSidebar from './components/FilterSidebar';
import AuctionDataGrid from './components/AuctionDataGrid';
import AuctionDetailPanel from './components/AuctionDetailPanel';
import BulkActionToolbar from './components/BulkActionToolbar';
import IntegrationStatusPanel from './components/IntegrationStatusPanel';

const RealTimeBiddingEngineDashboard = () => {
  const [auctions, setAuctions] = useState([]);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [selectedAuctions, setSelectedAuctions] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    state: '',
    bidRange: { min: null, max: null },
    buyerCount: '',
    search: '',
    highPriorityOnly: false,
    closingSoon: false
  });
  const [sortConfig, setSortConfig] = useState({ key: 'endTime', direction: 'asc' });
  const [savedPresets, setSavedPresets] = useState([
    { id: 1, name: 'High Value Auctions', filters: { bidRange: { min: 40, max: null }, status: 'active' } },
    { id: 2, name: 'Closing Soon', filters: { closingSoon: true, status: 'active' } },
    { id: 3, name: 'California Only', filters: { state: 'CA', status: 'active' } }
  ]);
  
  // UI State
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isSystemStatusExpanded, setIsSystemStatusExpanded] = useState(false);
  const [isIntegrationPanelCollapsed, setIsIntegrationPanelCollapsed] = useState(true);
  const [updatesPaused, setUpdatesPaused] = useState(false);

  // Initialize mock auction data
  useEffect(() => {
    const mockAuctions = [
      {
        id: 'AI-2024-0818-001',
        callMetadata: {
          type: 'Auto Insurance',
          state: 'CA',
          age: 35,
          gender: 'Male',
          zipCode: '90210',
          trafficSource: 'Google Ads',
          notes: 'High-intent lead with multiple vehicle quotes requested'
        },
        currentBid: 45.50,
        bidIncrement: 0.25,
        participatingBuyers: [
          { id: 'buyer-001', name: 'Premium Insurance Co.', lastBid: 45.50, responseTime: 120 },
          { id: 'buyer-002', name: 'SecureLife Partners', lastBid: 45.25, responseTime: 95 },
          { id: 'buyer-003', name: 'Guardian Insurance', lastBid: 45.00, responseTime: 180 }
        ],
        endTime: new Date(Date.now() + 180000), // 3 minutes
        status: 'active',
        priority: 'high'
      },
      {
        id: 'HO-2024-0818-002',
        callMetadata: {
          type: 'Home Insurance',
          state: 'TX',
          age: 42,
          gender: 'Female',
          zipCode: '75201',
          trafficSource: 'Facebook',
          notes: 'First-time homebuyer seeking comprehensive coverage'
        },
        currentBid: 32.75,
        bidIncrement: 0.25,
        participatingBuyers: [
          { id: 'buyer-004', name: 'TrustShield Agency', lastBid: 32.75, responseTime: 150 },
          { id: 'buyer-005', name: 'HomeGuard Insurance', lastBid: 32.50, responseTime: 200 }
        ],
        endTime: new Date(Date.now() + 420000), // 7 minutes
        status: 'active',
        priority: 'medium'
      },
      {
        id: 'LI-2024-0818-003',
        callMetadata: {
          type: 'Life Insurance',
          state: 'FL',
          age: 28,
          gender: 'Male',
          zipCode: '33101',
          trafficSource: 'TV Commercial',
          notes: 'Young professional seeking term life insurance'
        },
        currentBid: 28.25,
        bidIncrement: 0.25,
        participatingBuyers: [
          { id: 'buyer-006', name: 'LifeSecure Corp', lastBid: 28.25, responseTime: 85 },
          { id: 'buyer-007', name: 'FamilyFirst Insurance', lastBid: 28.00, responseTime: 110 },
          { id: 'buyer-008', name: 'PeaceMind Coverage', lastBid: 27.75, responseTime: 165 }
        ],
        endTime: new Date(Date.now() + 90000), // 1.5 minutes
        status: 'closing',
        priority: 'high'
      },
      {
        id: 'AI-2024-0818-004',
        callMetadata: {
          type: 'Auto Insurance',
          state: 'NY',
          age: 55,
          gender: 'Female',
          zipCode: '10001',
          trafficSource: 'Direct Mail',
          notes: 'Senior driver looking for better rates'
        },
        currentBid: 38.50,
        bidIncrement: 0.25,
        participatingBuyers: [
          { id: 'buyer-009', name: 'MetroShield Insurance', lastBid: 38.50, responseTime: 140 },
          { id: 'buyer-010', name: 'CityWide Coverage', lastBid: 38.25, responseTime: 175 }
        ],
        endTime: new Date(Date.now() + 600000), // 10 minutes
        status: 'active',
        priority: 'medium'
      },
      {
        id: 'HO-2024-0818-005',
        callMetadata: {
          type: 'Home Insurance',
          state: 'CA',
          age: 38,
          gender: 'Male',
          zipCode: '94102',
          trafficSource: 'Search Engine',
          notes: 'Homeowner seeking flood insurance coverage'
        },
        currentBid: 41.75,
        bidIncrement: 0.25,
        participatingBuyers: [
          { id: 'buyer-011', name: 'Pacific Insurance Group', lastBid: 41.75, responseTime: 105 },
          { id: 'buyer-012', name: 'WestCoast Protection', lastBid: 41.50, responseTime: 130 },
          { id: 'buyer-013', name: 'Golden State Insurance', lastBid: 41.25, responseTime: 190 }
        ],
        endTime: new Date(Date.now() + 300000), // 5 minutes
        status: 'active',
        priority: 'high'
      }
    ];

    // Generate additional mock auctions
    for (let i = 6; i <= 30; i++) {
      const types = ['Auto Insurance', 'Home Insurance', 'Life Insurance', 'Health Insurance'];
      const states = ['CA', 'TX', 'FL', 'NY', 'IL', 'PA', 'OH', 'GA', 'NC', 'MI'];
      const sources = ['Google Ads', 'Facebook', 'TV Commercial', 'Direct Mail', 'Search Engine', 'Radio'];
      const statuses = ['active', 'closing', 'completed'];
      const priorities = ['high', 'medium', 'low'];

      mockAuctions?.push({
        id: `${types?.[i % types?.length]?.split(' ')?.[0]?.toUpperCase()}-2024-0818-${i?.toString()?.padStart(3, '0')}`,
        callMetadata: {
          type: types?.[i % types?.length],
          state: states?.[i % states?.length],
          age: 25 + (i % 40),
          gender: i % 2 === 0 ? 'Male' : 'Female',
          zipCode: (10000 + i * 1000)?.toString(),
          trafficSource: sources?.[i % sources?.length],
          notes: `Lead ${i} - Standard inquiry for ${types?.[i % types?.length]?.toLowerCase()}`
        },
        currentBid: 20 + (i % 30) + Math.random() * 10,
        bidIncrement: 0.25,
        participatingBuyers: Array.from({ length: 1 + (i % 4) }, (_, j) => ({
          id: `buyer-${i}-${j}`,
          name: `Insurance Company ${i}-${j}`,
          lastBid: 20 + (i % 30) + Math.random() * 10,
          responseTime: 80 + Math.random() * 200
        })),
        endTime: new Date(Date.now() + (i % 10 + 1) * 60000),
        status: statuses?.[i % statuses?.length],
        priority: priorities?.[i % priorities?.length]
      });
    }

    setAuctions(mockAuctions);
  }, []);

  // Real-time updates simulation
  useEffect(() => {
    if (updatesPaused) return;

    const interval = setInterval(() => {
      setAuctions(prev => prev?.map(auction => {
        if (auction?.status === 'active' && Math.random() > 0.7) {
          const newBid = auction?.currentBid + 0.25;
          return {
            ...auction,
            currentBid: newBid,
            bidIncrement: 0.25,
            participatingBuyers: auction?.participatingBuyers?.map((buyer, index) => 
              index === 0 ? { ...buyer, lastBid: newBid } : buyer
            )
          };
        }
        return auction;
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, [updatesPaused]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event?.target?.tagName === 'INPUT' || event?.target?.tagName === 'TEXTAREA') return;

      switch (event?.key?.toLowerCase()) {
        case ' ':
          event?.preventDefault();
          setUpdatesPaused(!updatesPaused);
          break;
        case 'escape':
          setSelectedAuctions([]);
          setSelectedAuction(null);
          break;
        case 'enter':
          if (selectedAuction) {
            // Open auction details
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [updatesPaused, selectedAuction]);

  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  const handleSavePreset = useCallback((name, filters) => {
    const newPreset = {
      id: Date.now(),
      name,
      filters
    };
    setSavedPresets(prev => [...prev, newPreset]);
  }, []);

  const handleLoadPreset = useCallback((preset) => {
    setFilters(preset?.filters);
  }, []);

  const handleAuctionSelect = useCallback((auction) => {
    setSelectedAuction(auction);
  }, []);

  const handleBulkAction = useCallback(async (action, auctionIds) => {
    console.log(`Executing bulk action: ${action} on auctions:`, auctionIds);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Update auction states based on action
    setAuctions(prev => prev?.map(auction => {
      if (auctionIds?.includes(auction?.id)) {
        switch (action) {
          case 'pause':
            return { ...auction, status: 'paused' };
          case 'force_close':
            return { ...auction, status: 'completed' };
          case 'extend':
            return { 
              ...auction, 
              endTime: new Date(new Date(auction.endTime).getTime() + 120000) 
            };
          default:
            return auction;
        }
      }
      return auction;
    }));
    
    setSelectedAuctions([]);
  }, []);

  const handleAuctionAction = useCallback((action, auctionId) => {
    handleBulkAction(action, [auctionId]);
  }, [handleBulkAction]);

  const handleEmergencyAction = useCallback((action) => {
    console.log(`Emergency action: ${action}`);
    
    switch (action) {
      case 'enable':
        // Enable emergency mode
        break;
      case 'disable':
        // Disable emergency mode
        break;
      case 'pause_all':
        setUpdatesPaused(true);
        setAuctions(prev => prev?.map(auction => ({ ...auction, status: 'paused' })));
        break;
      default:
        break;
    }
  }, []);

  const handleQuickAction = useCallback((actionId, selectedItems) => {
    console.log(`Quick action: ${actionId}`, selectedItems);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Real-Time Bidding Engine Dashboard - Insure Connect</title>
        <meta name="description" content="Monitor and manage real-time insurance lead auctions with comprehensive bidding analytics and controls" />
      </Helmet>

      <Header 
        userRole="admin"
        isNotificationOpen={isNotificationOpen}
        onNotificationToggle={() => setIsNotificationOpen(!isNotificationOpen)}
      />

      <NotificationCenter 
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
      />

      <div className="pt-16">
        <SystemMetricsHeader onEmergencyAction={handleEmergencyAction} />
        
        <div className="flex h-[calc(100vh-8rem)]">
          <FilterSidebar
            filters={filters}
            onFiltersChange={handleFiltersChange}
            savedPresets={savedPresets}
            onSavePreset={handleSavePreset}
            onLoadPreset={handleLoadPreset}
          />
          
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="p-4 space-y-4">
              <QuickActionToolbar
                section="auction"
                selectedItems={selectedAuctions}
                onAction={handleQuickAction}
              />
              
              <BulkActionToolbar
                selectedAuctions={selectedAuctions}
                onBulkAction={handleBulkAction}
                onClearSelection={() => setSelectedAuctions([])}
              />
            </div>
            
            <div className="flex-1 px-4 pb-4 overflow-hidden">
              <AuctionDataGrid
                auctions={auctions}
                selectedAuction={selectedAuction}
                onAuctionSelect={handleAuctionSelect}
                filters={filters}
                sortConfig={sortConfig}
                onSort={handleSort}
              />
            </div>
          </div>
          
          <AuctionDetailPanel
            auction={selectedAuction}
            onClose={() => setSelectedAuction(null)}
            onAuctionAction={handleAuctionAction}
          />
        </div>
      </div>

      <SystemStatusIndicator
        isExpanded={isSystemStatusExpanded}
        onToggle={() => setIsSystemStatusExpanded(!isSystemStatusExpanded)}
      />

      <IntegrationStatusPanel
        isCollapsed={isIntegrationPanelCollapsed}
        onToggle={() => setIsIntegrationPanelCollapsed(!isIntegrationPanelCollapsed)}
      />
    </div>
  );
};

export default RealTimeBiddingEngineDashboard;